export const MEDICATIONS = [
  { id: "cypionate-200", label: "Testosterone cypionate 200 mg/mL", mgPerMl: 200 },
  { id: "enanthate-250", label: "Testosterone enanthate 250 mg/mL", mgPerMl: 250 },
  { id: "sustanon-250", label: "Sustanon 250 (mixed esters) 250 mg/mL", mgPerMl: 250 },
  { id: "custom", label: "Other concentration (enter below)", mgPerMl: null },
];

export const SYRINGES = [
  { id: "0.5", label: "0.5 mL insulin-type syringe", capacity: 0.5, majorStep: 0.1, minorStep: 0.01 },
  { id: "1", label: "1 mL syringe", capacity: 1, majorStep: 0.1, minorStep: 0.02 },
  { id: "3", label: "3 mL syringe", capacity: 3, majorStep: 0.5, minorStep: 0.1 },
];

export const DEFAULT_DEAD_SPACE_ML = 0.05;

export function calcSyringeFill({ mg, mgPerMl, deadSpaceMl, capacity }) {
  if (!mg || !mgPerMl || mg <= 0 || mgPerMl <= 0) return null;
  const doseMl = mg / mgPerMl;
  const totalMl = doseMl + (deadSpaceMl || 0);
  return {
    doseMl: round(doseMl, 3),
    totalMl: round(totalMl, 3),
    exceeds: totalMl > capacity,
    fillPct: Math.min(100, (totalMl / capacity) * 100),
    tooSmall: doseMl < 0.1 && capacity > 1,
  };
}

// --- Unit conversion -------------------------------------------------------
// Molecular-mass based factors used by UK/US laboratories.
export const UNIT_CONVERSIONS = [
  {
    id: "total-testosterone",
    name: "Total testosterone",
    from: "nmol/L",
    to: "ng/dL",
    factor: 28.84, // 1 nmol/L = 28.84 ng/dL
    ukRange: "Roughly 8–30 nmol/L (adult men, morning sample; assay-specific)",
    note: "UK laboratories report nmol/L; US laboratories report ng/dL. 'Normal' depends on the assay, timing and your symptoms.",
  },
  {
    id: "free-testosterone",
    name: "Free testosterone",
    from: "pmol/L",
    to: "pg/mL",
    factor: 0.2884, // 1 pmol/L = 0.2884 pg/mL
    ukRange: "Calculated free testosterone below ~225 pmol/L is often considered supportive of deficiency with symptoms",
    note: "Most UK results are calculated (Vermeulen) rather than measured directly.",
  },
  {
    id: "oestradiol",
    name: "Oestradiol (E2)",
    from: "pmol/L",
    to: "pg/mL",
    factor: 0.2724, // 1 pmol/L = 0.2724 pg/mL
    ukRange: "Typically under ~150 pmol/L in adult men (assay-specific)",
    note: "Standard immunoassays are imprecise at male concentrations; interpret trends with your clinician.",
  },
  {
    id: "shbg",
    name: "SHBG",
    from: "nmol/L",
    to: "µg/mL",
    factor: 0.0948, // approx; SHBG ~94.8 kDa
    ukRange: "Roughly 18–55 nmol/L in adult men (rises with age)",
    note: "SHBG is almost always reported in nmol/L; the mass unit is rarely used but included for completeness.",
  },
  {
    id: "prolactin",
    name: "Prolactin",
    from: "mIU/L",
    to: "ng/mL",
    factor: 1 / 21.2, // 1 ng/mL ≈ 21.2 mIU/L (WHO 3rd IS)
    ukRange: "Roughly under 400 mIU/L in men (assay-specific)",
    note: "A raised prolactin can be a cause of low testosterone and needs its own assessment.",
  },
  {
    id: "haemoglobin",
    name: "Haemoglobin",
    from: "g/L",
    to: "g/dL",
    factor: 0.1,
    ukRange: "Roughly 130–170 g/L in adult men",
    note: "UK reports g/L; many other countries use g/dL. Read alongside haematocrit when on TRT.",
  },
];

export function convertUnit(value, factor, reverse = false) {
  if (value === "" || value === null || Number.isNaN(Number(value))) return null;
  const v = Number(value);
  return reverse ? v / factor : v * factor;
}

// --- BMI -------------------------------------------------------------------
export const BMI_BANDS = [
  { max: 18.5, label: "Underweight", tone: "amber" },
  { max: 25, label: "Healthy weight", tone: "teal" },
  { max: 30, label: "Overweight", tone: "amber" },
  { max: 35, label: "Obesity class I", tone: "rose" },
  { max: 40, label: "Obesity class II", tone: "rose" },
  { max: Infinity, label: "Obesity class III", tone: "rose" },
];

export function calcBmi({ heightCm, weightKg }) {
  if (!heightCm || !weightKg || heightCm <= 0 || weightKg <= 0) return null;
  const m = heightCm / 100;
  const bmi = weightKg / (m * m);
  const band = BMI_BANDS.find((b) => bmi < b.max);
  return { bmi: round(bmi, 1), band };
}

export function waistToHeight(waistCm, heightCm) {
  if (!waistCm || !heightCm) return null;
  return round(waistCm / heightCm, 2);
}

export const ftInToCm = (ft, inch) => (Number(ft) || 0) * 30.48 + (Number(inch) || 0) * 2.54;
export const stLbToKg = (st, lb) => (Number(st) || 0) * 6.35029 + (Number(lb) || 0) * 0.453592;

export function round(n, dp) {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

export const TOOLS = [
  {
    slug: "adam-questionnaire",
    to: "/learn/adam-questionnaire",
    name: "ADAM questionnaire",
    kicker: "Symptom screen · 2 min",
    copy: "Ten yes/no questions clinicians use to decide whether a testosterone blood test is worthwhile — scored instantly and explained honestly.",
  },
  {
    slug: "unit-converter",
    to: "/tools/unit-converter",
    name: "Testosterone unit converter",
    kicker: "nmol/L ↔ ng/dL and more",
    copy: "Convert blood results between UK and international units for total and free testosterone, oestradiol, SHBG, prolactin and haemoglobin.",
  },
  {
    slug: "syringe-guide",
    to: "/tools/syringe-guide",
    name: "Syringe fill guide",
    kicker: "Injectable patients",
    copy: "Enter your prescribed dose and medication strength to see where to fill a 0.5, 1 or 3 mL syringe — dead space included.",
  },
  {
    slug: "bmi-calculator",
    to: "/tools/bmi-calculator",
    name: "BMI & waist calculator",
    kicker: "Metabolic context",
    copy: "Body mass index and waist-to-height ratio with the NHS categories — and why body fat is the biggest modifiable driver of testosterone.",
  },
];
